// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility functions for exporting asset exception data as CSV.
 * Provides client-side CSV generation for date and detail inconsistency reports.
 */
import { ExceptionType } from '@/constants/asset-exception-type';
import {
  DateInconsistencyItem,
  DetailInconsistencyItem,
} from '@/report/api/types';
import {
  buildCSVContent,
  escapeCSV,
  formatAmountForCSV,
  formatDateForCSV,
  generateCSVFilename,
  triggerCSVDownload,
} from '@/utilities/csv-export';

/**
 * Converts exception type codes to human-readable labels.
 *
 * @param exceptionTypes - Array of exception type codes
 * @returns Comma-separated string of human-readable labels
 */
function formatExceptionTypes(exceptionTypes: ExceptionType[]): string {
  const labels: Record<ExceptionType, string> = {
    IN_SERVICE_DATE_BEFORE_INVENTORY: 'In-service before inventory',
    FIRST_SALE_DATE_BEFORE_INVENTORY: 'Sale before inventory',
    DISPOSAL_DATE_BEFORE_INVENTORY: 'Disposal before inventory',
    FIRST_ASSIGNMENT_BEFORE_INVENTORY: 'Assignment before inventory',
    FIRST_MOVEMENT_BEFORE_INVENTORY: 'Movement before inventory',
    MISSING_COST_BASIS: 'Missing cost basis',
    DEPLOYMENT_WITHOUT_ACQUISITION: 'Deployment without acquisition',
    SALVAGE_EXCEEDS_50_PERCENT: 'Salvage exceeds 50%',
    MISSING_USEFUL_LIFE: 'Missing useful life',
  };

  return exceptionTypes.map((type) => labels[type] || type).join('; ');
}

/**
 * Exports date inconsistencies data to CSV.
 *
 * Columns: Serial, SKU, Product, Exception types, Inventory date,
 * In-service date, Sale date, Disposal date, First movement, First assignment
 *
 * @param assets - Array of date inconsistency items
 */
export function exportDateInconsistenciesCSV(
  assets: DateInconsistencyItem[]
): void {
  const headers = [
    'Serial',
    'SKU',
    'Product',
    'Exception types',
    'Inventory date',
    'In-service date',
    'First sale date',
    'Disposal date',
    'First movement date',
    'First assignment date',
  ];

  const rows = assets.map((item) => [
    escapeCSV(item.asset.serial),
    escapeCSV(item.asset.item.sku),
    escapeCSV(item.asset.item.name),
    escapeCSV(formatExceptionTypes(item.exceptionTypes)),
    formatDateForCSV(item.inventoryDate),
    formatDateForCSV(item.inServiceDate),
    formatDateForCSV(item.firstSaleDate),
    formatDateForCSV(item.scrapDate),
    formatDateForCSV(item.firstMovementDate),
    formatDateForCSV(item.firstAssignmentDate),
  ]);

  const csvContent = buildCSVContent(headers, rows);
  triggerCSVDownload(csvContent, generateCSVFilename('date-inconsistencies'));
}

/**
 * Exports detail inconsistencies data to CSV.
 *
 * Columns: Serial, SKU, Product, Exception types, In-service date,
 * Useful life (months), Currency, Inventory cost basis, Deployed cost basis,
 * Salvage value
 *
 * @param assets - Array of detail inconsistency items
 */
export function exportDetailInconsistenciesCSV(
  assets: DetailInconsistencyItem[]
): void {
  const headers = [
    'Serial',
    'SKU',
    'Product',
    'Exception types',
    'In-service date',
    'Useful life (months)',
    'Currency',
    'Inventory cost basis',
    'Deployed cost basis',
    'Salvage value',
  ];

  const rows = assets.map((item) => [
    escapeCSV(item.asset.serial),
    escapeCSV(item.asset.item.sku),
    escapeCSV(item.asset.item.name),
    escapeCSV(formatExceptionTypes(item.exceptionTypes)),
    formatDateForCSV(item.inServiceDate),
    item.usefulLife?.toString() ?? '',
    escapeCSV(item.costCurrencyCode),
    formatAmountForCSV(item.inventoryCostBasis),
    formatAmountForCSV(item.deployedCostBasis),
    formatAmountForCSV(item.salvageValue),
  ]);

  const csvContent = buildCSVContent(headers, rows);
  triggerCSVDownload(csvContent, generateCSVFilename('detail-inconsistencies'));
}
